import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  X,
  Bell,
  Users,
  CreditCard,
  AlertCircle,
  Clock,
  RotateCw,
} from 'lucide-react';
import { useUIStore } from '@/store/uiStore';
import { formatRelativeTime } from '@/lib/utils';
import { activityApi } from '@/lib/api';

interface NotificationItem {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  time: string;
  read: boolean;
  icon: 'user' | 'payment' | 'alert' | 'system';
}

const iconMap = {
  user: Users,
  payment: CreditCard,
  alert: AlertCircle,
  system: Bell,
};

const typeColors = {
  info: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
  success: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  warning: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  error: 'text-red-400 bg-red-500/10 border-red-500/30',
};

export default function NotificationPanel() {
  const { toggleNotificationPanel } = useUIStore();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setIsLoading(true);
      const response = await activityApi.getActivities({ limit: 20 });
      const activities = response.data.data || [];
      
      const mappedNotifications: NotificationItem[] = activities.map((activity: any) => {
        let type: 'info' | 'success' | 'warning' | 'error' = 'info';
        let icon: 'user' | 'payment' | 'alert' | 'system' = 'system';
        
        if (activity.type?.includes('error') || activity.type?.includes('failed')) {
          type = 'error';
          icon = 'alert';
        } else if (activity.type?.includes('success') || activity.type?.includes('approved')) {
          type = 'success';
          icon = 'payment';
        } else if (activity.type?.includes('warning') || activity.type?.includes('pending')) {
          type = 'warning';
          icon = 'user';
        } else if (activity.type?.includes('user')) {
          icon = 'user';
        } else if (activity.type?.includes('payment') || activity.type?.includes('transaction')) {
          icon = 'payment';
        }

        return {
          id: activity._id || activity.id || String(Math.random()),
          type,
          title: activity.title || activity.action || 'Activity',
          message: activity.description || activity.message || activity.details || '',
          time: activity.createdAt || activity.timestamp || new Date().toISOString(),
          read: activity.read || false,
          icon,
        };
      });

      setNotifications(mappedNotifications);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90]"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={toggleNotificationPanel}
      />

      {/* Panel */}
      <motion.div
        initial={{ x: 400 }}
        animate={{ x: 0 }}
        exit={{ x: 400 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="absolute right-0 top-0 h-screen w-full max-w-md bg-slate-900 border-l border-slate-800 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Bell className="w-5 h-5 text-violet-400" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
            <h2 className="text-lg font-semibold">Notifications</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchNotifications}
              disabled={isLoading}
              className="text-sm text-slate-400 hover:text-violet-400 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <RotateCw className="w-4 h-4 animate-spin" />
              ) : (
                'Refresh'
              )}
            </button>
            <button
              onClick={toggleNotificationPanel}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Notifications list */}
        <div className="h-[calc(100vh-8rem)] overflow-y-auto custom-scrollbar">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <RotateCw className="w-8 h-8 text-slate-600 animate-spin mb-4" />
              <h3 className="font-medium text-slate-400">Loading notifications...</h3>
            </div>
          ) : notifications.length > 0 ? (
            <div className="divide-y divide-slate-800/50">
              {notifications.map((notification) => {
                const Icon = iconMap[notification.icon];

                return (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`p-4 hover:bg-slate-800/30 cursor-pointer transition-colors ${!notification.read ? 'bg-slate-800/20' : ''
                      }`}
                  >
                    <div className="flex gap-4">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center border ${typeColors[notification.type]
                          }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className={`font-medium text-sm ${!notification.read ? 'text-white' : 'text-slate-300'}`}>
                            {notification.title}
                          </h3>
                          {!notification.read && (
                            <span className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-sm text-slate-400 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Clock className="w-3 h-3" />
                            {formatRelativeTime(notification.time)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center mb-4">
                <Bell className="w-8 h-8 text-slate-600" />
              </div>
              <h3 className="font-medium text-slate-400">No notifications</h3>
              <p className="text-sm text-slate-500 mt-1">
                You're all caught up!
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800 bg-slate-900">
          <button className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-sm font-medium transition-colors">
            View All Notifications
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
